import {
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Drawer,
  Group,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { IconCode, IconSparkles } from "@tabler/icons-react";
import { useCreateTranslations } from "../../api/translations";
import { describeLocation } from "./describeLocation";

/**
 * Side drawer with AI metadata for a translation key: description,
 * AI-generated translation drafts (with an "Accept draft" action) and
 * where the key is used.
 *
 * `meta` is the key's entry from GET /translations/ui/meta, written by
 * scripts/enrich-translation-keys.js in the PR housekeeping job.
 */
function KeyMetaDrawer({ record, meta, validLanguages, opened, onClose }) {
  const { mutate: createTranslations, isPending } = useCreateTranslations();

  const handleAcceptDraft = (languageCode) => {
    createTranslations({
      translationKey: record.translationKey,
      translations: [{ languageCode, value: meta.suggestions[languageCode] }],
    });
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title={
        <Group gap="xs">
          <Code fw={700}>{record?.translationKey}</Code>
          <Badge size="xs" variant="outline" color="gray">
            AI
          </Badge>
        </Group>
      }
    >
      {!record ? null : !meta ? (
        <Text size="sm" c="dimmed">
          No AI metadata available for this key yet. It will be generated on the
          next translations sync.
        </Text>
      ) : (
        <Stack gap="md">
          <Text size="sm">{meta.description}</Text>

          <Paper withBorder p="sm" radius="sm">
            <Group gap={6} mb="xs">
              <IconSparkles size={14} />
              <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                AI translation drafts
              </Text>
            </Group>

            <Stack gap={6}>
              {validLanguages.map((lang) => {
                const currentValue = record.languages?.[lang];
                const suggestion = meta.suggestions?.[lang];

                return (
                  <Group key={lang} gap="sm" wrap="nowrap">
                    <Text fw={700} size="xs" tt="uppercase" w={24}>
                      {lang}
                    </Text>

                    {currentValue ? (
                      <>
                        <Text size="sm" style={{ flex: 1 }} lineClamp={1}>
                          {currentValue}
                        </Text>
                        <Badge color="green" variant="light" size="sm">
                          Translated
                        </Badge>
                      </>
                    ) : suggestion ? (
                      <>
                        <Code style={{ flex: 1 }}>{suggestion}</Code>
                        <Button
                          size="compact-xs"
                          loading={isPending}
                          onClick={() => handleAcceptDraft(lang)}
                        >
                          Accept draft
                        </Button>
                      </>
                    ) : (
                      <Text
                        size="sm"
                        c="dimmed"
                        fs="italic"
                        style={{ flex: 1 }}
                      >
                        No draft available
                      </Text>
                    )}
                  </Group>
                );
              })}
            </Stack>

            <Divider my="xs" />
            <Text size="xs" c="dimmed">
              Drafts are generated by AI from code context. Accepting saves the
              value as unpublished; publish via the normal version flow.
            </Text>
          </Paper>

          <Paper withBorder p="sm" radius="sm">
            <Group gap={6} mb="xs">
              <IconCode size={14} />
              <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                Where used
              </Text>
            </Group>

            <Stack gap="xs">
              {meta.locations.map((loc) => {
                const { app, color, label } = describeLocation(loc);
                return (
                  <Box key={loc.file}>
                    <Group gap={6} wrap="nowrap" mb={4}>
                      <Badge
                        size="xs"
                        variant="outline"
                        color={color}
                        style={{ flexShrink: 0 }}
                      >
                        {app}
                      </Badge>
                      {loc.screen && <Text size="xs">{label}</Text>}
                    </Group>
                    <Code block fz="xs">
                      {loc.file}
                    </Code>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Stack>
      )}
    </Drawer>
  );
}

export default KeyMetaDrawer;
